import { Head, Link, router, usePage } from "@inertiajs/react";
import { ArrowLeft, Edit, Globe, Package, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { SubcategoryList } from "@/components/subcategory-list";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import AppLayout from "@/layouts/app-layout";
import {
  editOrganizationCategoryPath,
  organizationCategoriesPath,
  shareOrganizationCategoryPath,
} from "@/routes";
import type { BreadcrumbItem, Category, SharedData } from "@/types";

interface CategoryShowProps {
  category: Category & {
    subcategories: {
      id: number;
      name: string;
      description?: string;
      created_at: string;
    }[];
    suppliers: {
      id: number;
      name: string;
    }[];
  };
}

export default function CategoryShow({ category }: CategoryShowProps) {
  const { auth } = usePage<SharedData>().props;
  const organizationSlug = auth.current_organization?.slug ?? "";
  const currentOrganizationId = auth.current_organization?.id ?? null;
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const categoriesHref = organizationCategoriesPath(organizationSlug);
  const editCategoryHref = editOrganizationCategoryPath(organizationSlug, category);
  const shareHref = shareOrganizationCategoryPath(organizationSlug, category);
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Categorías",
      href: categoriesHref,
    },
    {
      title: category.name,
      href: "#",
    },
  ];
  const canShare =
    category.visibility === "private" &&
    category.owner_organization_id === currentOrganizationId;

  const handleShare = () => {
    setIsSharing(true);
    router.patch(shareHref, undefined, {
      onFinish: () => {
        setIsSharing(false);
        setIsShareDialogOpen(false);
      },
      onError: () => {
        toast.error("No se pudo compartir la categoría");
      },
    });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${category.name} - Categorías`} />
      <div className="flex h-full flex-1 flex-col gap-6 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link href={categoriesHref}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {category.name}
              </h1>
              {category.description && (
                <p className="text-muted-foreground">
                  {category.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canShare && (
              <Button onClick={() => setIsShareDialogOpen(true)}>
                <Globe className="mr-2 h-4 w-4" />
                Compartir categoría
              </Button>
            )}
            <Button asChild>
              <Link href={editCategoryHref}>
                <Edit className="h-4 w-4 mr-2" />
                Editar Categoría
              </Link>
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Subcategorías
              </CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {category.subcategories?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                subcategorías activas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Proveedores</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {category.suppliers?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                proveedores asignados
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Subcategories Management */}
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold">Subcategorías</h2>
            <p className="text-muted-foreground">
              Administra las subcategorías para esta categoría
            </p>
          </div>

          <Separator />

          <SubcategoryList
            category={category}
            subcategories={category.subcategories || []}
          />
        </div>

        {/* Associated Suppliers */}
        {category.suppliers && category.suppliers.length > 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Proveedores Asociados</h2>
              <p className="text-muted-foreground">
                Proveedores que pertenecen a esta categoría
              </p>
            </div>

            <Separator />

            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {category.suppliers.map((supplier) => (
                <Card key={supplier.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{supplier.name}</CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Compartir categoría públicamente</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Una vez compartida, esta categoría será visible para todas las
                organizaciones que tengan habilitado el catálogo público.
              </p>
              <p>
                Ya no podrás editarla directamente. Cualquier cambio deberá
                hacerse como override local.
              </p>
              <p className="font-medium text-amber-600 dark:text-amber-400">
                Esta acción es irreversible.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSharing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleShare} disabled={isSharing}>
              {isSharing ? "Compartiendo..." : "Compartir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
